import { NavLink } from 'react-router';
import { useAuth } from 'react-oidc-context';
import { beginSignOut, cognitoLogoutUrl } from '@/lib/auth-config';
import { useHealth } from '@/lib/useHealth';
import { canAccessAdmin, canBrowseTaxonomy, canImportData, canVisualize, useMe } from '@/lib/useMe';
import { isPageMobile, isPageVisible, useAnonymousRead, usePagesConfig } from '@/lib/usePages';
import { useIsMobile } from '@/lib/useIsMobile';
import { visibleNavEntries } from '@/lib/navEntries';
import { cn } from '@/lib/utils';
import { AboutMenu } from './AboutMenu';
import { MainMenu } from './MainMenu';
import { useCurrentDatabase, useDatabaseSwitchEffects } from '@/lib/useDatabases';
import { buttonVariants } from '@/components/ui/Button';

export function Header() {
  const auth = useAuth();
  const me = useMe();
  const health = useHealth();

  const handleLogout = async () => {
    // Latch BEFORE removeUser: clearing the user re-renders RequireAuth as
    // unauthenticated, and its auto sign-in must not race our /logout
    // navigation (it sometimes won — the SSO cookie then signed the user
    // straight back in).
    beginSignOut();
    await auth.removeUser();
    window.location.href = cognitoLogoutUrl();
  };

  const pagesConfig = usePagesConfig();
  const servesGuests = useAnonymousRead().data === true;
  const role = me.data?.role;
  // The name beside "Connected" on a site with more than one database, as the
  // desktop's header named the profile it was connected to: the user reads
  // what every page is addressing without opening the selector.
  const currentDatabase = useCurrentDatabase();
  useDatabaseSwitchEffects();
  const status = connectionState(
    health,
    currentDatabase.count > 1 ? (currentDatabase.database?.name ?? null) : null,
  );
  // Toggleable pages hide from the nav when the admin disabled them or
  // scoped them away from this role. Always-on pages ignore this. On a
  // phone-sized viewport the server page registry's mobile flag also
  // applies, so the nav never offers a page that cannot work there.
  const isMobile = useIsMobile();
  const entries = visibleNavEntries(
    (key) =>
      isPageVisible(pagesConfig.data, key, role) &&
      (!isMobile || isPageMobile(pagesConfig.data, key)),
    {
      canImport: auth.isAuthenticated && canImportData(me.data),
      canConsole: auth.isAuthenticated && canAccessAdmin(me.data),
      canVisualize: canVisualize(me.data),
      canBrowseTaxonomy: canBrowseTaxonomy(me.data),
    },
  );

  return (
    <header>
      {/* Top band — near-black, logo + branding + connection status */}
      <div className="bg-tres-header-bg text-white">
        <div className="container mx-auto flex h-14 items-center justify-between gap-3 md:gap-6">
          {/* Logo and wordmark are the way home — the Welcome nav entry was
              removed in favour of the convention every site already teaches. */}
          <NavLink to="/" className="flex min-w-0 items-center gap-3 rounded hover:opacity-90">
            <img
              src="/tres-logo.png"
              alt="TReS"
              className="h-8 w-8 shrink-0 rounded"
              draggable={false}
            />
            <div className="flex items-baseline gap-3">
              <span className="text-xl font-semibold tracking-tight">TReS</span>
              <span className="hidden text-sm text-white/90 sm:inline">
                LLM Grounding Platform
              </span>
            </div>
          </NavLink>

          <div className="flex shrink-0 items-center gap-2 text-sm sm:gap-4">
            {/* The database every page reads is named beside "Connected" on a
                site with more than one; switching it is a deliberate step on
                Data Management, never a control here (36.12.21). */}
            {/* The version lives in the Welcome-page footer and the About
                dialog now — off the always-visible header (2026-09-04). */}
            <span
              className={cn('inline-flex items-center gap-1.5', status.colorClass)}
              title={status.label}
            >
              <span className={cn('h-2 w-2 rounded-full', status.dotClass)} />
              <span className="hidden sm:inline">{status.label}</span>
            </span>

            {auth.isAuthenticated ? (
              <>
                <AboutMenu version={health.data?.version} />
                <span className="hidden text-white/70 md:inline">
                  {auth.user?.profile.email ?? auth.user?.profile.sub ?? 'signed in'}
                </span>
                <button
                  type="button"
                  onClick={handleLogout}
                  className={buttonVariants({ variant: 'chrome', size: 'md' })}
                >
                  Sign out
                </button>
              </>
            ) : (
              servesGuests && (
                <>
                  <AboutMenu version={health.data?.version} />
                  <span className="hidden text-white/70 sm:inline">Viewing as guest</span>
                  <button
                    type="button"
                    onClick={() => void auth.signinRedirect()}
                    className={buttonVariants({ variant: 'chrome', size: 'md' })}
                  >
                    Sign in
                  </button>
                </>
              )
            )}
          </div>
        </div>
      </div>

      {/* Menu row — dark blue, page nav.
          Grouped by what the user is doing rather than by when each page was
          built: define the model, ask it questions, read the results, run the
          place. Separators are the grouping — a divider costs one rule and
          saves the reader parsing a flat row of eleven. Where the row does not
          fit — a phone, or a narrow window — it collapses behind one button: a
          scrolling strip has no affordance. */}
      {(auth.isAuthenticated || servesGuests) &&
        <MainMenu entries={entries} collapsed={isMobile} />}
    </header>
  );
}

function connectionState(
  health: ReturnType<typeof useHealth>,
  database: string | null,
): {
  label: string;
  colorClass: string;
  dotClass: string;
} {
  const to = database ? ` to ${database}` : '';
  if (health.isLoading) {
    return {
      label: 'Connecting…',
      colorClass: 'text-tres-status-pending',
      dotClass: 'bg-tres-status-pending animate-pulse',
    };
  }
  if (health.error) {
    return {
      label: 'Offline',
      colorClass: 'text-tres-status-error',
      dotClass: 'bg-tres-status-error',
    };
  }
  // The process answers but the index does not reflect the data — a rebuild
  // was refused for lack of room or stopped by the build breaker.
  if (health.data?.status === 'degraded') {
    return {
      label: `Connected${to} · index degraded`,
      colorClass: 'text-tres-status-error',
      dotClass: 'bg-tres-status-error',
    };
  }
  return {
    label: `Connected${to}`,
    colorClass: 'text-tres-status-active',
    dotClass: 'bg-tres-status-active',
  };
}
